import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
var _excluded = ["routes", "params", "exception", "isPreRouter"];
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { customRequestIdleCallback } from '@zenlayer/util';
import { useLatest } from 'ahooks';
import { treeToArr } from '@zenlayer/zen-design/esm/_util/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

function addFullPath(routes) {
  var parentPath = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  return routes.map(function (route) {
    var fullPath = "".concat(parentPath).concat(route.path.startsWith('/') ? route.path : '/' + route.path);

    var updatedRoute = _objectSpread(_objectSpread({}, route), {}, {
      fullPath: fullPath
    });

    if (route.children) {
      updatedRoute.children = addFullPath(route.children, fullPath);
    }

    return updatedRoute;
  });
}

function organizeRoutes(list, params, isPreRenderRoutes) {
  return list.map(function (r) {
    var _r$children, _r$priority;

    var path = r.path.replace(/^\/+|\/+$/g, ''); // 去除起始和结尾的斜杠

    var authorization = r.authorize ? r.authorize(params) : true;
    var children = (_r$children = r.children) !== null && _r$children !== void 0 && _r$children.length ? organizeRoutes(r.children, params, isPreRenderRoutes) : undefined;
    var hasAvailableChild = children ? children.some(function (child) {
      return child.authorization && child.priority >= 0;
    }) : false;
    var priority = r.component || hasAvailableChild ? (_r$priority = r.priority) !== null && _r$priority !== void 0 ? _r$priority : path ? 0 : Infinity : -1;
    var component = r.component;

    if (isPreRenderRoutes) {
      var _r$preRender;

      if (!((_r$preRender = r.preRender) !== null && _r$preRender !== void 0 && _r$preRender.length)) {
        component = undefined;
      }
    } else {
      var _r$preRender2;

      if (!!((_r$preRender2 = r.preRender) !== null && _r$preRender2 !== void 0 && _r$preRender2.length)) {
        component = undefined;
      }
    }

    return _objectSpread(_objectSpread({}, r), {}, {
      component: component,
      path: path,
      priority: priority,
      authorization: authorization,
      children: children
    });
  });
}

function createRouteElements(_ref) {
  var _defaultRoute;

  var name = _ref.name,
      routes = _ref.routes,
      exception = _ref.exception,
      loadingElement = _ref.loadingElement;

  if (!routes.length) {
    return [];
  }

  var defaultRoute = null;
  var is404Exists = false;
  var elements = routes.map(function (r) {
    var _r$children2;

    if (r.authorization && r.priority >= 0 && (!defaultRoute || r.priority > defaultRoute.priority)) {
      defaultRoute = r;
    }

    if (r.path === '*') {
      is404Exists = true;
    }

    return /*#__PURE__*/_jsx(Route, {
      path: r.path,
      element: r.authorization ? r.component ? /*#__PURE__*/_jsx(React.Suspense, {
        children: /*#__PURE__*/React.createElement(r.component)
      }) : undefined : /*#__PURE__*/_jsx(Navigate, {
        to: exception[403],
        replace: true
      }),
      children: (_r$children2 = r.children) !== null && _r$children2 !== void 0 && _r$children2.length ? createRouteElements({
        name: r.path,
        routes: r.children,
        exception: exception,
        loadingElement: loadingElement
      }) : undefined
    }, r.path);
  });

  if ((_defaultRoute = defaultRoute) !== null && _defaultRoute !== void 0 && _defaultRoute.path) {
    elements.unshift( /*#__PURE__*/_jsx(Route, {
      path: "",
      element: /*#__PURE__*/_jsx(Navigate, {
        to: defaultRoute.path,
        replace: true
      })
    }, "".concat(name, "/index")));
  }

  if (!is404Exists) {
    elements.push( /*#__PURE__*/_jsx(Route, {
      path: "*",
      element: /*#__PURE__*/_jsx(Navigate, {
        to: exception[404],
        replace: true
      })
    }, "".concat(name, "/404")));
  }

  return elements;
}
/**
 * 根据传入的路由配置和参数，生成符合react router组件所需的路由元素数组
 * @component
 * @param routers 路由配置对象，包括path、priority、component、children、authorize
 * @param params 用于动态路由和授权的参数
 * @param exception  处理403和404错误
 */


var BaseRouter = function BaseRouter(_ref2) {
  var routes = _ref2.routes,
      params = _ref2.params,
      exception = _ref2.exception,
      isPreRouter = _ref2.isPreRouter,
      rest = _objectWithoutProperties(_ref2, _excluded);

  var elements = React.useMemo(function () {
    var _exception$, _exception$2;

    var _exception = {
      403: (_exception$ = exception === null || exception === void 0 ? void 0 : exception[403]) !== null && _exception$ !== void 0 ? _exception$ : '/exception?code=403',
      404: (_exception$2 = exception === null || exception === void 0 ? void 0 : exception[404]) !== null && _exception$2 !== void 0 ? _exception$2 : '/exception?code=404'
    };
    return createRouteElements({
      name: '#',
      // 没有特殊意义，随便取的名字
      routes: organizeRoutes(routes, params, isPreRouter),
      exception: _exception
    });
  }, [routes, params, JSON.stringify(exception)]);
  return /*#__PURE__*/_jsx(Routes, _objectSpread(_objectSpread({}, rest), {}, {
    children: elements
  }));
};

var PreRouter = function PreRouter(props) {
  var routes = useMemo(function () {
    return [{
      path: '/null'
    }].concat(_toConsumableArray(props.routes));
  }, [props.routes]);
  var location = useLocation();

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      show = _useState2[0],
      setShow = _useState2[1];

  var _useState3 = useState(),
      _useState4 = _slicedToArray(_useState3, 2),
      preLocation = _useState4[0],
      setPreLocation = _useState4[1];

  var latestPreLocation = useLatest(preLocation);
  var Timer = useRef();

  var _routes = useMemo(function () {
    return treeToArr(addFullPath(organizeRoutes(routes, props.params, true) || []), 'fullPath').filter(function (i) {
      var _i$preRender;

      return !!((_i$preRender = i.preRender) !== null && _i$preRender !== void 0 && _i$preRender.length);
    });
  }, [props.routes, props.params]);

  useEffect(function () {
    clearInterval(Timer.current);

    var _show = _routes.some(function (i) {
      return i.fullPath === location.pathname;
    });

    setShow(_show);

    if (_show) {
      if (latestPreLocation && (!!location.search || !!location.hash || !!location.state)) {
        setPreLocation({
          pathname: '/null'
        });
        setTimeout(function () {
          setPreLocation(undefined);
        }, 10);
      } else {
        setPreLocation(undefined);
      }
    } else {
      if (show) {
        setPreLocation(undefined);
      }

      var preRouteInfo = _routes.find(function (i) {
        var _i$preRender2;

        return i.authorization && ((_i$preRender2 = i.preRender) === null || _i$preRender2 === void 0 ? void 0 : _i$preRender2.some(function (p) {
          return location.pathname.includes(p);
        }));
      });

      if (preRouteInfo) {
        Timer.current = window.setInterval(function () {
          clearInterval(Timer.current);
          customRequestIdleCallback(function () {
            setPreLocation({
              hash: '',
              pathname: preRouteInfo.fullPath,
              search: '?isPreRender=true',
              state: undefined
            });
          });
        }, 3000);
      }
    }

    return function () {
      clearInterval(Timer.current);
    };
  }, [location.pathname]);

  if (!_routes.length) {
    return null;
  }

  return /*#__PURE__*/_jsx("div", {
    hidden: !show,
    style: {
      width: '100%',
      display: show ? 'flex' : 'none',
      flexDirection: 'column',
      flexGrow: 1,
      alignItems: 'center'
    },
    children: /*#__PURE__*/_jsx(BaseRouter, _objectSpread(_objectSpread({}, props), {}, {
      routes: routes,
      location: preLocation || location,
      isPreRouter: true
    }))
  });
};

export var Router = function Router(props) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(BaseRouter, _objectSpread({}, props)), /*#__PURE__*/_jsx(PreRouter, _objectSpread({}, props))]
  });
};